import type {
  Apparatus,
  Bag,
  BagInventoryItem,
  CatalogSearchResult,
  StockLotChoice,
  StockLotItem,
  DailyTruckCheckDetails,
  DailyTruckCheckHistoryRow,
  DailyTruckCheckTaskVerificationResult,
  TruckCheckDetails,
  TruckCheckHistoryRow,
  TruckCheckTaskVerificationResult
} from "./types";
import { recordApiFailure, recordApiReachable, recordApiSuccess } from "./networkStatus";
import type { StockSaveRow } from "./stockSave";

export interface ApiError {
  code: string;
  message: string;
  details?: unknown;
  status?: number;
  retryable?: boolean;
}

export interface ApiRequestOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
  dedupe?: boolean;
  retry?: boolean;
  cacheTtlMs?: number;
  staleWhileRevalidateMs?: number;
  forceRefresh?: boolean;
}

export const DEFAULT_API_TIMEOUT_MS = 12_000;
export const DEFAULT_MUTATION_TIMEOUT_MS = 30_000;
export const MAX_GET_RETRIES = 2;

const BASE_RETRY_DELAY_MS = 250;
const MAX_RETRY_AFTER_MS = 2_000;
const LONG_MUTATION_TIMEOUT_MS = 60_000;
const BACKUP_MUTATION_TIMEOUT_MS = 120_000;
const inFlightGets = new Map<string, Promise<unknown>>();
const lastGoodGets = new Map<string, { data: unknown; storedAt: number }>();
const cacheWriteGenerations = new Map<string, number>();
let cacheGeneration = 0;

export class ApiRequestError extends Error implements ApiError {
  code: string;
  details?: unknown;
  status?: number;
  retryable?: boolean;

  constructor(
    code: string,
    message: string,
    options: { details?: unknown; status?: number; retryable?: boolean; cause?: unknown } = {}
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = "ApiRequestError";
    this.code = code;
    this.details = options.details;
    this.status = options.status;
    this.retryable = options.retryable;
  }
}

function isBrowserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function isRetryableStatus(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

function retryDelayMs(retryNumber: number, response?: Response): number {
  const retryAfter = response?.headers.get("retry-after");
  if (retryAfter) {
    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
      return Math.min(MAX_RETRY_AFTER_MS, Math.round(seconds * 1_000));
    }
  }

  const exponential = BASE_RETRY_DELAY_MS * 2 ** Math.max(0, retryNumber - 1);
  const jitter = Math.floor(Math.random() * BASE_RETRY_DELAY_MS);
  return exponential + jitter;
}

function waitForRetry(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }

    const onAbort = () => {
      globalThis.clearTimeout(timeoutId);
      reject(signal.reason);
    };
    const timeoutId = globalThis.setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function baseRequestKey(path: string, method: string, headers: Headers): string {
  const normalizedHeaders = [...headers.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
  return [method, path, normalizedHeaders].join("::");
}

function requestKey(
  path: string,
  method: string,
  headers: Headers,
  options: ApiRequestOptions,
  cacheMode?: RequestCache
): string {
  return [
    baseRequestKey(path, method, headers),
    `timeout=${options.timeoutMs ?? DEFAULT_API_TIMEOUT_MS}`,
    `retry=${options.retry !== false}`,
    `force=${options.forceRefresh === true}`,
    `cache=${cacheMode ?? "default"}`
  ].join("::");
}

function callerAbort<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) {
    return Promise.reject(new ApiRequestError("ABORTED", "Request was cancelled."));
  }

  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(new ApiRequestError("ABORTED", "Request was cancelled."));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
  });
}

interface AbortState {
  controller: AbortController;
  reason: "timeout" | "caller" | null;
  cleanup: () => void;
}

function createAbortState(timeoutMs: number, callerSignal?: AbortSignal): AbortState {
  const controller = new AbortController();
  const state: AbortState = {
    controller,
    reason: null,
    cleanup: () => undefined
  };

  const timeoutId = globalThis.setTimeout(() => {
    state.reason = "timeout";
    controller.abort(new DOMException("Request timed out", "TimeoutError"));
  }, timeoutMs);

  const onCallerAbort = () => {
    if (state.reason) return;
    state.reason = "caller";
    controller.abort(callerSignal?.reason);
  };

  if (callerSignal) {
    if (callerSignal.aborted) {
      onCallerAbort();
    } else {
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }

  state.cleanup = () => {
    globalThis.clearTimeout(timeoutId);
    callerSignal?.removeEventListener("abort", onCallerAbort);
  };

  return state;
}

function abortError(state: AbortState, safeGet: boolean): ApiRequestError {
  if (state.reason === "timeout") {
    return safeGet
      ? new ApiRequestError("TIMEOUT", "The request took too long. Check the connection and try again.", {
          retryable: true
        })
      : new ApiRequestError(
          "TIMEOUT",
          "The request took too long, but the change may have been saved. Refresh this view before trying again.",
          {
            retryable: false
          }
        );
  }
  return new ApiRequestError("ABORTED", "Request was cancelled.");
}

function networkError(error: unknown, mutationMayHaveCompleted = false): ApiRequestError {
  if (mutationMayHaveCompleted) {
    return new ApiRequestError(
      "MUTATION_UNCONFIRMED",
      "The connection was lost before the change was confirmed. It may have been saved. Reconnect and refresh this view before trying again.",
      {
        retryable: false,
        cause: error
      }
    );
  }
  if (isBrowserOffline()) {
    return new ApiRequestError("OFFLINE", "You appear to be offline. Reconnect and try again.", {
      retryable: true,
      cause: error
    });
  }
  return new ApiRequestError("NETWORK_ERROR", "The server could not be reached. Check the connection and try again.", {
    retryable: true,
    cause: error
  });
}

function reportRequestFailure(error: ApiRequestError): void {
  if (error.code !== "ABORTED") {
    recordApiFailure(error.code);
  }
}

function invalidateReadState(): void {
  cacheGeneration += 1;
  inFlightGets.clear();
  lastGoodGets.clear();
  cacheWriteGenerations.clear();
}

function isUnconfirmedMutationError(error: ApiRequestError): boolean {
  return error.code === "MUTATION_UNCONFIRMED" || error.code === "TIMEOUT";
}

async function executeRequest<T>(
  path: string,
  init: RequestInit,
  options: ApiRequestOptions,
  callerSignal?: AbortSignal
): Promise<T> {
  if (isBrowserOffline()) {
    const error = networkError(undefined);
    reportRequestFailure(error);
    throw error;
  }

  const method = (init.method ?? "GET").toUpperCase();
  const safeGet = method === "GET";
  const retriesAllowed = safeGet && options.retry !== false ? MAX_GET_RETRIES : 0;
  const defaultTimeoutMs = safeGet ? DEFAULT_API_TIMEOUT_MS : DEFAULT_MUTATION_TIMEOUT_MS;
  const abortState = createAbortState(options.timeoutMs ?? defaultTimeoutMs, callerSignal);
  let retryCount = 0;

  try {
    while (true) {
      let response: Response;
      try {
        response = await fetch(path, {
          ...init,
          signal: abortState.controller.signal
        });
      } catch (error) {
        if (abortState.controller.signal.aborted) {
          const mapped = abortError(abortState, safeGet);
          reportRequestFailure(mapped);
          throw mapped;
        }

        const mapped = networkError(error, !safeGet);
        if (mapped.code !== "OFFLINE" && retryCount < retriesAllowed) {
          retryCount += 1;
          reportRequestFailure(mapped);
          await waitForRetry(retryDelayMs(retryCount), abortState.controller.signal);
          continue;
        }
        reportRequestFailure(mapped);
        throw mapped;
      }

      let payload: any = null;
      try {
        payload = await response.json();
      } catch (error) {
        if (abortState.controller.signal.aborted) throw error;
        const mapped = networkError(error, !safeGet);
        if (mapped.code !== "OFFLINE" && retryCount < retriesAllowed) {
          retryCount += 1;
          reportRequestFailure(mapped);
          await waitForRetry(retryDelayMs(retryCount), abortState.controller.signal);
          continue;
        }
        reportRequestFailure(mapped);
        throw mapped;
      }
      if (response.ok && payload?.ok) {
        if (!safeGet) {
          invalidateReadState();
        }
        recordApiSuccess();
        return payload.data as T;
      }

      const retryable = isRetryableStatus(response.status);
      if (retryable && retryCount < retriesAllowed) {
        retryCount += 1;
        recordApiFailure(`HTTP_${response.status}`);
        await waitForRetry(retryDelayMs(retryCount, response), abortState.controller.signal);
        continue;
      }

      const payloadError = payload?.error;
      const mapped =
        !safeGet && retryable
          ? new ApiRequestError(
              "MUTATION_UNCONFIRMED",
              "The server returned an error before the change was confirmed. It may have been saved. Refresh this view before trying again.",
              {
                details: payloadError?.details,
                status: response.status,
                retryable: false
              }
            )
          : new ApiRequestError(
              payloadError?.code ?? `HTTP_${response.status || "ERROR"}`,
              payloadError?.message || response.statusText || "Request failed",
              {
                details: payloadError?.details,
                status: response.status,
                retryable
              }
            );
      if (retryable) reportRequestFailure(mapped);
      else recordApiReachable();
      throw mapped;
    }
  } catch (error) {
    if (error instanceof ApiRequestError) {
      if (!safeGet && isUnconfirmedMutationError(error)) invalidateReadState();
      throw error;
    }
    if (abortState.controller.signal.aborted) {
      const mapped = abortError(abortState, safeGet);
      reportRequestFailure(mapped);
      if (!safeGet && isUnconfirmedMutationError(mapped)) invalidateReadState();
      throw mapped;
    }
    const mapped = networkError(error, !safeGet);
    reportRequestFailure(mapped);
    if (!safeGet && isUnconfirmedMutationError(mapped)) invalidateReadState();
    throw mapped;
  } finally {
    abortState.cleanup();
  }
}

export function request<T>(
  path: string,
  init: RequestInit = {},
  adminToken?: string,
  stockEditToken?: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const headers = new Headers(init?.headers);
  if (init.body != null && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (adminToken) {
    headers.set("x-admin-token", adminToken);
  }
  if (stockEditToken) {
    headers.set("x-stock-edit-token", stockEditToken);
  }

  const requestInit: RequestInit = {
    ...init,
    headers
  };
  const callerSignal = options.signal ?? requestInit.signal ?? undefined;
  const method = (requestInit.method ?? "GET").toUpperCase();
  if (method === "GET" && options.forceRefresh) {
    requestInit.cache = "no-cache";
  }
  if (method !== "GET") {
    return executeRequest<T>(path, requestInit, options, callerSignal);
  }

  // A caller-owned signal must control its own network fetch. Cached values remain
  // safe to share, while only signal-free GETs share the underlying in-flight work.
  const dedupe = options.dedupe !== false && !callerSignal;
  const cacheKey = baseRequestKey(path, method, headers);
  if (options.forceRefresh) {
    cacheWriteGenerations.set(cacheKey, (cacheWriteGenerations.get(cacheKey) ?? 0) + 1);
  }
  const key = requestKey(path, method, headers, options, requestInit.cache);
  const requestCacheGeneration = cacheGeneration;
  const requestCacheWriteGeneration = cacheWriteGenerations.get(cacheKey) ?? 0;
  const cacheTtlMs = Math.max(0, options.cacheTtlMs ?? 0);
  const staleWhileRevalidateMs = Math.max(0, options.staleWhileRevalidateMs ?? 0);
  const cached = lastGoodGets.get(cacheKey) as { data: T; storedAt: number } | undefined;
  const cacheAge = cached ? Date.now() - cached.storedAt : Number.POSITIVE_INFINITY;

  const loadShared = () => {
    if (!dedupe) {
      return executeRequest<T>(path, requestInit, options, callerSignal);
    }

    let shared = inFlightGets.get(key) as Promise<T> | undefined;
    if (!shared) {
      shared = executeRequest<T>(path, requestInit, options).finally(() => {
        if (inFlightGets.get(key) === shared) inFlightGets.delete(key);
      });
      inFlightGets.set(key, shared);
    }
    return shared;
  };

  const storeLastGood = (data: T) => {
    if (
      requestCacheGeneration === cacheGeneration &&
      requestCacheWriteGeneration === (cacheWriteGenerations.get(cacheKey) ?? 0) &&
      (cacheTtlMs > 0 || staleWhileRevalidateMs > 0)
    ) {
      lastGoodGets.set(cacheKey, { data, storedAt: Date.now() });
    }
    return data;
  };

  if (!options.forceRefresh && cached && cacheTtlMs > 0 && cacheAge <= cacheTtlMs) {
    return callerAbort(Promise.resolve(cached.data), callerSignal);
  }

  if (
    !options.forceRefresh &&
    cached &&
    staleWhileRevalidateMs > 0 &&
    cacheAge <= cacheTtlMs + staleWhileRevalidateMs
  ) {
    if (!callerSignal?.aborted) {
      void loadShared().then(storeLastGood).catch(() => undefined);
    }
    return callerAbort(Promise.resolve(cached.data), callerSignal);
  }

  return callerAbort(loadShared().then(storeLastGood), callerSignal);
}

export function resetApiRequestStateForTests(): void {
  invalidateReadState();
}

export interface ApparatusDirectoryEntry extends Apparatus {
  bags: Bag[];
}

export interface ApparatusInventorySearchResult {
  apparatus: { id: string; name: string };
  query: string;
  limit: number;
  truncated: boolean;
  matches: Array<{
    bagId: string;
    bagName: string;
    item: BagInventoryItem;
  }>;
}

export interface ComplianceDeficiencyItem {
  apparatusId: string;
  apparatusName: string;
  catalogItemId: string;
  itemName: string;
  category: string | null;
  subcategory: string | null;
  bagNames: string | null;
  requiredQty: number;
  actualQty: number;
  gapQty: number;
  expiredQty: number;
  expiring90Qty: number;
  status: "yellow" | "red";
  deficiencyType: "missing" | "low_quantity" | "expired" | "expiring_90d";
  alertOnly: boolean;
  countsForCompliance: boolean;
}

export interface ComplianceDeficienciesResult {
  generatedAt: string;
  apparatus: Array<{
    apparatusId: string;
    apparatusName: string;
    redCount: number;
    yellowCount: number;
    isNoncompliant: boolean;
    items: ComplianceDeficiencyItem[];
  }>;
}

const REFERENCE_CACHE_TTL_MS = 30_000;
const REFERENCE_STALE_MS = 5 * 60_000;
const LIVE_CACHE_TTL_MS = 15_000;
const LIVE_STALE_MS = 60_000;

function referenceRequestOptions(options?: ApiRequestOptions): ApiRequestOptions {
  return {
    cacheTtlMs: REFERENCE_CACHE_TTL_MS,
    staleWhileRevalidateMs: REFERENCE_STALE_MS,
    ...options
  };
}

function liveRequestOptions(options?: ApiRequestOptions): ApiRequestOptions {
  return {
    cacheTtlMs: LIVE_CACHE_TTL_MS,
    staleWhileRevalidateMs: LIVE_STALE_MS,
    ...options
  };
}

export const api = {
  health: (options?: ApiRequestOptions) =>
    request<{ status: string; env: string }>("/api/health", undefined, undefined, undefined, options),

  getApparatus: (options?: ApiRequestOptions) =>
    request<{ apparatus: Apparatus[] }>(
      "/api/apparatus",
      undefined,
      undefined,
      undefined,
      referenceRequestOptions(options)
    ),
  getBags: (apparatusId: string, options?: ApiRequestOptions) =>
    request<{ apparatus: { id: string; name: string }; bags: Bag[] }>(
      `/api/apparatus/${apparatusId}/bags`,
      undefined,
      undefined,
      undefined,
      referenceRequestOptions(options)
    ),
  getApparatusDirectory: (options?: ApiRequestOptions) =>
    request<{ apparatus: ApparatusDirectoryEntry[] }>(
      "/api/apparatus/directory",
      undefined,
      undefined,
      undefined,
      referenceRequestOptions(options)
    ),
  searchApparatusInventory: (
    apparatusId: string,
    q: string,
    limit = 60,
    options?: ApiRequestOptions
  ) =>
    request<ApparatusInventorySearchResult>(
      `/api/apparatus/${encodeURIComponent(apparatusId)}/inventory-search?q=${encodeURIComponent(q)}&limit=${limit}`,
      undefined,
      undefined,
      undefined,
      options
    ),

  getBagInventory: (bagId: string, q = "", options?: ApiRequestOptions) =>
    request<{
      bag: { id: string; name: string; apparatusId: string; apparatusName: string; complianceScope: "qrs" | "non_qrs" };
      items: BagInventoryItem[];
    }>(
      `/api/bags/${bagId}/inventory${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      undefined,
      undefined,
      undefined,
      options
    ),

  getStockInventory: (q = "", options?: ApiRequestOptions) =>
    request<{ location: string; items: StockLotItem[] }>(
      `/api/stock${q ? `?q=${encodeURIComponent(q)}` : ""}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  getStockLotsForCatalogItem: (catalogItemId: string, options?: ApiRequestOptions) =>
    request<{ catalogItemId: string; items: StockLotChoice[] }>(
      `/api/stock/lots?catalogItemId=${encodeURIComponent(catalogItemId)}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  getAllowedStockItems: (q: string, limit = 25, options?: ApiRequestOptions) =>
    request<{ query: string; items: CatalogSearchResult[] }>(
      `/api/stock/allowed-items?q=${encodeURIComponent(q)}&limit=${limit}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  verifyStockPin: (pin: string) => request<{ token: string }>("/api/stock/unlock", { method: "POST", body: JSON.stringify({ pin }) }),
  allowStockItem: (body: { catalogItemId: string; enteredName: string; note?: string | null }, stockEditToken?: string) =>
    request<{ created: boolean; allowListId: string; catalogItemId: string }>("/api/stock/allow-item", {
      method: "POST",
      body: JSON.stringify(body)
    }, undefined, stockEditToken),
  addStockLot: (body: any, stockEditToken?: string) =>
    request<{ lot: any }>("/api/stock/add", { method: "POST", body: JSON.stringify(body) }, undefined, stockEditToken),
  bulkUpdateStock: (
    body: {
      enteredName: string;
      note?: string | null;
      rows: StockSaveRow[];
    },
    stockEditToken?: string
  ) =>
    request<{
      updatedCount: number;
      updatedStockCount: number;
      updatedParCount: number;
      unchangedCount: number;
      requestedCount: number;
      updatedStockLotIds: string[];
      updatedParCatalogItemIds: string[];
    }>(
      "/api/stock/bulk-update",
      { method: "POST", body: JSON.stringify(body) },
      undefined,
      stockEditToken,
      { timeoutMs: LONG_MUTATION_TIMEOUT_MS }
    ),
  updateStockLot: (body: any, stockEditToken?: string) =>
    request<{ lot: any }>("/api/stock/update", { method: "POST", body: JSON.stringify(body) }, undefined, stockEditToken),

  searchCatalog: (q: string, limit = 25, options?: ApiRequestOptions) =>
    request<{ query: string; items: CatalogSearchResult[] }>(
      `/api/catalog/search?q=${encodeURIComponent(q)}&limit=${limit}`,
      undefined,
      undefined,
      undefined,
      options
    ),

  addInventoryRecord: (body: any) => request<{ record: any }>("/api/inventory/add", { method: "POST", body: JSON.stringify(body) }),
  applyApparatusItemAction: (body: any) =>
    request<{ destinationRecord: any; replacedRecordId: string | null; remainingStockQty: number | null }>(
      "/api/inventory/apparatus-item",
      { method: "POST", body: JSON.stringify(body) }
    ),
  updateInventoryRecord: (body: any) =>
    request<{ record: any }>("/api/inventory/update", { method: "POST", body: JSON.stringify(body) }),
  splitInventoryRecord: (body: any) =>
    request<{ sourceRecord: any | null; destinationRecord: any; splitQuantity: number }>("/api/inventory/split", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  removeInventory: (body: any) => request<{ removed: boolean }>("/api/inventory/remove", { method: "POST", body: JSON.stringify(body) }),

  moveStockToBag: (body: any, stockEditToken?: string) =>
    request<any>("/api/movement/stock-to-bag", { method: "POST", body: JSON.stringify(body) }, undefined, stockEditToken),
  moveBagToBag: (body: any) => request<any>("/api/movement/bag-to-bag", { method: "POST", body: JSON.stringify(body) }),
  moveBagToStock: (body: any) => request<any>("/api/movement/bag-to-stock", { method: "POST", body: JSON.stringify(body) }),

  verifyAdminPin: (pin: string) => request<{ token: string }>("/api/admin/verify-pin", { method: "POST", body: JSON.stringify({ pin }) }),

  getComplianceSummary: (options?: ApiRequestOptions) =>
    request<any>("/api/compliance/summary", undefined, undefined, undefined, options),
  getComplianceDeficiencies: (apparatusId?: string, options?: ApiRequestOptions) =>
    request<ComplianceDeficienciesResult>(
      `/api/compliance/deficiencies${apparatusId ? `?apparatusId=${encodeURIComponent(apparatusId)}` : ""}`,
      undefined,
      undefined,
      undefined,
      liveRequestOptions(options)
    ),
  getApparatusCompliance: (apparatusId?: string, options?: ApiRequestOptions) =>
    request<any>(
      `/api/compliance/apparatus${apparatusId ? `?apparatusId=${encodeURIComponent(apparatusId)}` : ""}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  getOverallCompliance: (options?: ApiRequestOptions) =>
    request<any>("/api/compliance/overall", undefined, undefined, undefined, options),
  getSimpleOrderList: (options?: ApiRequestOptions) =>
    request<any>("/api/orders/simple?compact=1", undefined, undefined, undefined, liveRequestOptions(options)),
  getChangeLogHistory: (q = "", actionType = "", limit = 50, cursor = "", options?: ApiRequestOptions) =>
    request<any>(
      `/api/changelog/history?q=${encodeURIComponent(q)}&actionType=${encodeURIComponent(actionType)}&limit=${limit}&compact=1${
        cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""
      }`,
      undefined,
      undefined,
      undefined,
      options
    ),
  revertChangeLog: (body: { mode: "single" | "to_point"; transactionId: string; enteredName: string; confirmationText: string }) =>
    request<any>(
      "/api/changelog/revert",
      { method: "POST", body: JSON.stringify(body) },
      undefined,
      undefined,
      { timeoutMs: BACKUP_MUTATION_TIMEOUT_MS }
    ),
  importSystemBackup: (body: { csv: string; enteredName: string; confirmationText: string }) =>
    request<any>(
      "/api/backup/import",
      { method: "POST", body: JSON.stringify(body) },
      undefined,
      undefined,
      { timeoutMs: BACKUP_MUTATION_TIMEOUT_MS }
    ),

  getActiveTruckCheck: (apparatusId: string, options?: ApiRequestOptions) =>
    request<{ check: TruckCheckDetails | null }>(
      `/api/truck-checks/active?apparatusId=${encodeURIComponent(apparatusId)}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  startTruckCheck: (body: { apparatusId: string; enteredName: string; note?: string | null }) =>
    request<TruckCheckDetails>(
      "/api/truck-checks/start",
      { method: "POST", body: JSON.stringify(body) },
      undefined,
      undefined,
      { timeoutMs: LONG_MUTATION_TIMEOUT_MS }
    ),
  getTruckCheck: (id: string, options?: ApiRequestOptions) =>
    request<TruckCheckDetails>(
      `/api/truck-checks/${encodeURIComponent(id)}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  verifyTruckCheckTask: (
    checkId: string,
    body: {
      taskId: string;
      enteredName: string;
      observedQuantity?: number | null;
      observedExpirationDate?: string | null;
      observedNotes?: string | null;
      bagExistsChecked?: boolean | null;
      note?: string | null;
      sourceType?: "stock" | "other" | null;
      stockLotId?: string | null;
      stockTransferQuantity?: number | null;
      resolutionAction?: "verify" | "add_expiration" | "delete";
      applyToInventory?: boolean;
      checkRevision?: string | null;
    }
  ) =>
    request<TruckCheckTaskVerificationResult>(`/api/truck-checks/${encodeURIComponent(checkId)}/verify-task?compact=1`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  completeTruckCheck: (checkId: string, body: { enteredName: string; signoffDate?: string | null; note?: string | null }) =>
    request<TruckCheckDetails>(`/api/truck-checks/${encodeURIComponent(checkId)}/complete`, { method: "POST", body: JSON.stringify(body) }),
  abandonTruckCheck: (checkId: string, body: { enteredName: string; note?: string | null }) =>
    request<TruckCheckDetails>(`/api/truck-checks/${encodeURIComponent(checkId)}/abandon`, { method: "POST", body: JSON.stringify(body) }),
  reopenTruckCheck: (checkId: string, body: { enteredName: string; note?: string | null }) =>
    request<TruckCheckDetails>(`/api/truck-checks/${encodeURIComponent(checkId)}/reopen`, { method: "POST", body: JSON.stringify(body) }),
  deleteAbandonedTruckCheck: (checkId: string) =>
    request<{ deleted: true; checkId: string }>(`/api/truck-checks/${encodeURIComponent(checkId)}/delete`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  getTruckCheckHistory: (
    apparatusId = "",
    status: "all" | "in_progress" | "completed" | "abandoned" = "all",
    limit = 100,
    options?: ApiRequestOptions
  ) =>
    request<{ rows: TruckCheckHistoryRow[] }>(
      `/api/truck-checks/history?apparatusId=${encodeURIComponent(apparatusId)}&status=${encodeURIComponent(status)}&limit=${limit}`,
      undefined,
      undefined,
      undefined,
      options
    ),

  getActiveDailyTruckCheck: (apparatusId: string, options?: ApiRequestOptions) =>
    request<{ check: DailyTruckCheckDetails | null }>(
      `/api/daily-truck-checks/active?apparatusId=${encodeURIComponent(apparatusId)}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  startDailyTruckCheck: (body: { apparatusId: string; enteredName: string; note?: string | null }) =>
    request<DailyTruckCheckDetails>(
      "/api/daily-truck-checks/start",
      { method: "POST", body: JSON.stringify(body) },
      undefined,
      undefined,
      { timeoutMs: LONG_MUTATION_TIMEOUT_MS }
    ),
  getDailyTruckCheck: (id: string, options?: ApiRequestOptions) =>
    request<DailyTruckCheckDetails>(
      `/api/daily-truck-checks/${encodeURIComponent(id)}`,
      undefined,
      undefined,
      undefined,
      options
    ),
  verifyDailyTruckCheckTask: (
    checkId: string,
    body: {
      taskId: string;
      enteredName: string;
      observedQuantity?: number | null;
      observedExpirationDate?: string | null;
      observedNotes?: string | null;
      visualStatus?: "verified" | "problem" | null;
      note?: string | null;
      sourceType?: "stock" | "other" | null;
      stockLotId?: string | null;
      stockTransferQuantity?: number | null;
      resolutionAction?: "verify" | "add_expiration" | "delete";
      applyToInventory?: boolean;
      checkRevision?: string | null;
    }
  ) =>
    request<DailyTruckCheckTaskVerificationResult>(`/api/daily-truck-checks/${encodeURIComponent(checkId)}/verify-task?compact=1`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  completeDailyTruckCheck: (checkId: string, body: { enteredName: string; signoffDate?: string | null; note?: string | null }) =>
    request<DailyTruckCheckDetails>(`/api/daily-truck-checks/${encodeURIComponent(checkId)}/complete`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  abandonDailyTruckCheck: (checkId: string, body: { enteredName: string; note?: string | null }) =>
    request<DailyTruckCheckDetails>(`/api/daily-truck-checks/${encodeURIComponent(checkId)}/abandon`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  reopenDailyTruckCheck: (checkId: string, body: { enteredName: string; note?: string | null }) =>
    request<DailyTruckCheckDetails>(`/api/daily-truck-checks/${encodeURIComponent(checkId)}/reopen`, {
      method: "POST",
      body: JSON.stringify(body)
    }),
  deleteAbandonedDailyTruckCheck: (checkId: string) =>
    request<{ deleted: true; checkId: string }>(`/api/daily-truck-checks/${encodeURIComponent(checkId)}/delete`, {
      method: "POST",
      body: JSON.stringify({})
    }),
  getDailyTruckCheckHistory: (
    apparatusId = "",
    status: "all" | "in_progress" | "completed" | "abandoned" = "all",
    limit = 100,
    options?: ApiRequestOptions
  ) =>
    request<{ rows: DailyTruckCheckHistoryRow[] }>(
      `/api/daily-truck-checks/history?apparatusId=${encodeURIComponent(apparatusId)}&status=${encodeURIComponent(status)}&limit=${limit}`,
      undefined,
      undefined,
      undefined,
      options
    ),

  getAuditHistory: (adminToken: string, q = "", actionType = "", limit = 200, options?: ApiRequestOptions) =>
    request<any>(
      `/api/audit/history?q=${encodeURIComponent(q)}&actionType=${encodeURIComponent(actionType)}&limit=${limit}`,
      undefined,
      adminToken,
      undefined,
      options
    ),

  addCatalogItem: (adminToken: string, body: any) =>
    request<{ id: string }>("/api/admin/catalog-item", { method: "POST", body: JSON.stringify(body) }, adminToken),
  getBuildInfo: (adminToken: string, options?: ApiRequestOptions) =>
    request<any>("/api/admin/build-info", undefined, adminToken, undefined, options)
};
